/**
 * Hook for searching design chat sessions from ERP
 * Uses SEARCH_DESIGN_CHAT_SESSIONS endpoint
 */

import { useState, useCallback } from 'react';
import axios from 'axios';
import { getApiEndpoint } from '@/utils/ERPUtils';

import { logger } from '@/utils/logger';
export interface ChatSession {
  name: string; // ERPNext document ID (use this as session_id)
  session_id?: string;
  session_title: string;
  customer: string;
  property: string;
  reference_doc: string;
  status: string;
  category: string; // Chat category (e.g., 'General')
  started_at: string;
  ended_at: string | null;
  total_messages: number;
  total_cost: number;
}

interface SearchResult {
  success: boolean;
  sessions: ChatSession[];
  total_count: number;
}

interface UseChatSessionSearchReturn {
  sessions: ChatSession[];
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  searchSessions: (query: string) => Promise<void>;
  clearResults: () => void;
}

export const useChatSessionSearch = (): UseChatSessionSearchReturn => {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const searchSessions = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSessions([]);
      setTotalCount(0);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        getApiEndpoint('SEARCH_DESIGN_CHAT_SESSIONS'),
        { session_title: query },
        { withCredentials: true }
      );

      const data = response.data?.message;

      if (data?.success) {
        setSessions(data.sessions || []);
        setTotalCount(data.total_count || data.sessions?.length || 0);
      } else {
        setError(data?.message || 'Search failed');
        setSessions([]);
        setTotalCount(0);
      }
    } catch (err: any) {
      logger.error('[useChatSessionSearch] Search failed:', err);
      setError(err.message || 'Failed to search chat sessions');
      setSessions([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearResults = useCallback(() => {
    setSessions([]);
    setTotalCount(0);
    setError(null);
  }, []);

  return {
    sessions,
    isLoading,
    error,
    totalCount,
    searchSessions,
    clearResults,
  };
};

export default useChatSessionSearch;
